import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActions = ({ onAction }) => {
  const [showMore, setShowMore] = useState(false);

  const primaryActions = [
    {
      id: 'add-item',
      title: 'Add Collectible',
      description: 'Add a new item to your collection',
      icon: 'Plus',
      color: 'primary',
      href: '/add-collectible'
    },
    {
      id: 'market-analysis',
      title: 'Market Analysis',
      description: 'View market trends and insights',
      icon: 'TrendingUp',
      color: 'success',
      href: '/market-analysis'
    },
    {
      id: 'bulk-update',
      title: 'Bulk Update',
      description: 'Update multiple items at once',
      icon: 'Edit',
      color: 'warning'
    },
    {
      id: 'export-data',
      title: 'Export Data',
      description: 'Download your collection data',
      icon: 'Download',
      color: 'secondary'
    }
  ];

  const secondaryActions = [
    {
      id: 'import-data',
      title: 'Import Collection',
      description: 'Import items from CSV or other sources',
      icon: 'Upload',
      color: 'primary'
    },
    {
      id: 'backup',
      title: 'Backup Data',
      description: 'Create a backup of your collection',
      icon: 'Shield',
      color: 'trust-builder'
    },
    {
      id: 'share-collection',
      title: 'Share Collection',
      description: 'Share your collection with others',
      icon: 'Share',
      color: 'conversion-accent'
    },
    {
      id: 'settings',
      title: 'Collection Settings',
      description: 'Manage collection preferences',
      icon: 'Settings',
      color: 'muted'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: 'bg-primary/10 text-primary border-primary/20',
      success: 'bg-success/10 text-success border-success/20',
      warning: 'bg-warning/10 text-warning border-warning/20',
      secondary: 'bg-secondary/10 text-secondary-foreground border-secondary/20',
      'trust-builder': 'bg-trust-builder/10 text-trust-builder border-trust-builder/20',
      'conversion-accent': 'bg-conversion-accent/10 text-conversion-accent border-conversion-accent/20',
      muted: 'bg-muted/50 text-muted-foreground border-muted'
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  const handleActionClick = (action) => {
    if (action?.href) {
      window.location.href = action?.href;
    } else if (onAction) {
      onAction(action?.id);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-card-foreground">Quick Actions</h3>
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-sm text-primary hover:text-primary/80 transition-colors duration-200"
        >
          {showMore ? 'Show Less' : 'More Actions'}
        </button>
      </div>
      {/* Primary Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {primaryActions?.map((action) => (
          <button
            key={action?.id}
            onClick={() => handleActionClick(action)}
            className={`p-4 rounded-lg border transition-all duration-300 premium-hover text-left ${getColorClasses(action?.color)} hover:scale-105`}
          >
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(action?.color)}`}>
                <Icon name={action?.icon} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium mb-1">{action?.title}</h4>
                <p className="text-sm opacity-80">{action?.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
      {/* Secondary Actions */}
      {showMore && (
        <div className="space-y-3 pt-6 border-t border-border animate-slide-up">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Additional Actions
          </h4>
          <div className="grid grid-cols-1 gap-3">
            {secondaryActions?.map((action) => (
              <button
                key={action?.id}
                onClick={() => handleActionClick(action)}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200 text-left"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getColorClasses(action?.color)}`}>
                  <Icon name={action?.icon} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="text-sm font-medium text-card-foreground">{action?.title}</h5>
                  <p className="text-xs text-muted-foreground">{action?.description}</p>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Featured Action */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="bg-gradient-to-r from-primary/10 to-warning/10 rounded-lg p-4 border border-primary/20">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Sparkles" size={20} className="text-primary-foreground" />
            </div>
            <div>
              <h4 className="font-medium text-card-foreground">AI Collection Insights</h4>
              <p className="text-sm text-muted-foreground">Get personalized recommendations</p>
            </div>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
            className="w-full"
            onClick={() => handleActionClick({ id: 'ai-insights' })}
          >
            Get AI Insights
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;